/**
 * Tour controller (#124) — the live state behind a guided tour over the REAL
 * app pages.
 *
 * A single context, provided once high in the authed shell, holds:
 *   - the active `TutorialDefinition` (or null when no tour is running),
 *   - the current `stepIndex`,
 *   - `start(id)` / `next()` / `back()` / `stop()` — plus `restart()` and
 *     `finish()` for the polished resume + completion flow (#122 follow-up).
 *
 * It deliberately knows NOTHING about the DOM or navigation — the global
 * `<TourOverlay/>` (mounted alongside the provider) observes this state,
 * navigates to each step's `route`, waits for its `data-tour` anchor to mount,
 * and paints the Spotlight + StepCard.
 *
 * RESUME / RESTART (#122 follow-up):
 *   - The current step is persisted to localStorage (progress.setProgress) on
 *     every move and on exit, so a tour exited partway can RESUME where it left
 *     off. `start(id)` resumes from the saved step (clamped); a completed tour
 *     or one with no saved progress starts at step 1.
 *   - `restart()` jumps back to step 1 and wipes the saved pointer.
 *   - `finish()` records completion (progress.markDone, which also clears the
 *     in-progress pointer) and stops. The StepCard shows a short completion
 *     celebration on the final step; its "Done" calls finish().
 *
 * SAFETY: the controller only mutates its own in-memory step pointer + the
 * localStorage progress. It never touches the API, never auto-clicks, never
 * submits.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getTutorial, type TutorialDefinition, type TutorialStep } from "./definitions";
import { getResumeStep, markDone, setProgress } from "./progress";

export interface TourState {
  /** The running tutorial, or null when idle. */
  tutorial: TutorialDefinition | null;
  /** Index into `tutorial.steps`. Meaningless when `tutorial` is null. */
  stepIndex: number;
  /** Convenience: the current step (null when idle). */
  step: TutorialStep | null;
  /** Whether a tour is active. */
  isActive: boolean;
  /** Whether the current step is the tour's last (false when idle). */
  isLast: boolean;
  /** True when `start` resumed an in-progress tour past its first step. */
  resumed: boolean;
  /**
   * Begin a tour by id, RESUMING from any saved in-progress step (clamped to
   * the live step count). A completed tour, or one with no saved progress,
   * starts at step 1. No-op for an unknown id.
   */
  start: (id: string) => void;
  /** Advance one step. On the last step this is a no-op (use `finish`). */
  next: () => void;
  /** Go back one step (clamped at the first). */
  back: () => void;
  /** Jump back to step 1 of the running tour and clear saved progress. */
  restart: () => void;
  /** Record completion (localStorage) and stop. Call from the final step. */
  finish: () => void;
  /** Stop without completing (skip / exit / Esc). Saved progress is kept. */
  stop: () => void;
}

const TourContext = createContext<TourState | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [tutorial, setTutorial] = useState<TutorialDefinition | null>(null);
  const [stepIndex, setStepIndexState] = useState(0);
  const [resumed, setResumed] = useState(false);

  // Move to a step AND persist it, so an exit mid-tour can resume here. Step 0
  // clears the pointer (resuming at the very start == a fresh start).
  const goToStep = useCallback((id: string, index: number) => {
    setStepIndexState(index);
    setProgress(id, index);
  }, []);

  const start = useCallback((id: string) => {
    const def = getTutorial(id);
    if (!def) return;
    // Resume from the saved step, clamped to the current definition length
    // (the tour may have gained/lost steps since the pointer was written).
    const saved = getResumeStep(id);
    const resumeAt = Math.min(Math.max(0, saved), def.steps.length - 1);
    setTutorial(def);
    setStepIndexState(resumeAt);
    setResumed(resumeAt > 0);
    // Re-persist the (possibly clamped) resume point.
    setProgress(id, resumeAt);
  }, []);

  const stop = useCallback(() => {
    // Keep the in-progress pointer so the tour can be resumed later; just
    // tear down the live UI.
    setTutorial(null);
    setStepIndexState(0);
    setResumed(false);
  }, []);

  const next = useCallback(() => {
    if (!tutorial) return;
    const isLast = stepIndex >= tutorial.steps.length - 1;
    // On the last step, advancing is a no-op — completion goes through
    // `finish()` (the StepCard shows a celebration first). This keeps Next
    // from silently ending the tour without the wrap-up.
    if (isLast) return;
    setResumed(false);
    goToStep(tutorial.id, stepIndex + 1);
  }, [tutorial, stepIndex, goToStep]);

  const back = useCallback(() => {
    if (!tutorial) return;
    setResumed(false);
    goToStep(tutorial.id, Math.max(0, stepIndex - 1));
  }, [tutorial, stepIndex, goToStep]);

  const restart = useCallback(() => {
    if (!tutorial) return;
    setResumed(false);
    goToStep(tutorial.id, 0); // step 0 also clears the saved pointer
  }, [tutorial, goToStep]);

  const finish = useCallback(() => {
    if (!tutorial) return;
    // markDone also clears the in-progress pointer for this tour.
    markDone(tutorial.id);
    setTutorial(null);
    setStepIndexState(0);
    setResumed(false);
  }, [tutorial]);

  const value = useMemo<TourState>(
    () => ({
      tutorial,
      stepIndex,
      step: tutorial ? (tutorial.steps[stepIndex] ?? null) : null,
      isActive: tutorial !== null,
      isLast: tutorial !== null && stepIndex >= tutorial.steps.length - 1,
      resumed,
      start,
      next,
      back,
      restart,
      finish,
      stop,
    }),
    [tutorial, stepIndex, resumed, start, next, back, restart, finish, stop],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/** Access the tour controller. Throws if used outside <TourProvider>. */
export function useTour(): TourState {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour must be used inside <TourProvider>");
  }
  return ctx;
}
